/* eslint-disable jsx-a11y/anchor-has-content */
/* eslint-disable jsx-a11y/heading-has-content */
/* eslint-disable react/jsx-props-no-spreading */
import React, { useState } from 'react';
import cn from '../../utils/styleHelpers';
import { withDraftMentions } from './messages/injectDraftMentions';

// Utility to strip react-markdown internal props
const filterProps = (props: Record<string, any>) => {
  const newProps = { ...props };
  if ('node' in newProps) delete (newProps as any).node;
  return newProps;
};

const UnorderedList = ({ className, ...props }: any) => (
  <ul
    // Reduced padding for cleaner look, added space-y for breathing room
    className={cn(className, 'text-sm leading-6 flex list-disc flex-col pl-6 space-y-1 my-2 text-gray-700')}
    {...filterProps(props)}
  />
);

const OrderedList = ({ className, ...props }: any) => (
  <ol
    className={cn(className, 'text-sm leading-6 flex list-decimal flex-col pl-6 space-y-1 my-2 text-gray-700')}
    {...filterProps(props)}
  />
);

const Paragraph = ({ className, children, ...props }: any) => (
  <p className={cn(className, 'text-sm leading-6 text-gray-700 mb-2 last:mb-0')} {...filterProps(props)}>
    {withDraftMentions(children, 'p')}
  </p>
);

const EmphasizedText = ({ className, ...props }: any) => (
  <em className={cn(className, 'text-sm font-medium text-gray-800 italic')} {...filterProps(props)} />
);

const ItalicText = ({ className, ...props }: any) => (
  <i className={cn(className, 'italic text-sm leading-6 text-gray-700')} {...filterProps(props)} />
);

const StrongText = ({ className, ...props }: any) => (
  <strong className={cn(className, 'text-sm font-bold text-gray-900')} {...filterProps(props)} />
);

const BoldText = ({ className, ...props }: any) => (
  <b className={cn(className, 'text-sm font-bold text-gray-900')} {...filterProps(props)} />
);

const UnderlinedText = ({ className, ...props }: any) => (
  <u className={cn(className, 'underline text-sm leading-6 decoration-gray-400')} {...filterProps(props)} />
);

const DeletedText = ({ className, ...props }: any) => (
  <del className={cn(className, 'text-gray-400 line-through text-sm leading-6')} {...filterProps(props)} />
);

const HorizontalRule = ({ className, ...props }: any) => (
  <hr className={cn(className, 'w-full my-6 border-t border-gray-200')} {...filterProps(props)} />
);

const Blockquote = ({ className, ...props }: any) => (
  <blockquote className={cn(className, 'text-sm leading-6 pl-4 border-l-2 border-gray-300 italic text-gray-600 my-2')} {...filterProps(props)} />
);

const AnchorLink = ({ className, ...props }: any) => (
  <a
    className={cn(className, 'cursor-pointer text-sm font-medium text-indigo-600 hover:text-indigo-700 hover:underline')}
    target="_blank"
    rel="noopener noreferrer"
    {...filterProps(props)}
  />
);

const InlineCode = ({ className, children, ...props }: any) => (
  <code className={cn(className, "relative whitespace-pre-wrap rounded-md bg-gray-100 px-1.5 py-0.5 text-xs font-medium text-gray-800 border border-gray-200")} {...filterProps(props)}>
    {children}
  </code>
);

const ListItem = ({ className, children, ...props }: any) => (
  <li className={cn(className)} {...filterProps(props)}>
    {withDraftMentions(children, 'li')}
  </li>
);

const Heading1 = ({ className, ...props }: any) => (
  <h1 className={cn(className, 'text-lg font-bold text-gray-900 py-2')} {...filterProps(props)} />
);
const Heading2 = ({ className, ...props }: any) => (
  <h2 className={cn(className, 'text-base font-bold text-gray-900 py-2')} {...filterProps(props)} />
);
const Heading3 = ({ className, ...props }: any) => (
  <h3 className={cn(className, 'text-sm font-bold text-gray-900 py-0.5')} {...filterProps(props)} />
);
const Heading4 = ({ className, ...props }: any) => (
  <h4 className={cn(className, 'text-sm font-semibold text-gray-900 py-1')} {...filterProps(props)} />
);
const Heading5 = ({ className, ...props }: any) => (
  <h5 className={cn(className, 'text-xs font-semibold text-gray-900 py-1')} {...filterProps(props)} />
);
const Heading6 = ({ className, ...props }: any) => (
  <h6 className={cn(className, 'text-xs font-semibold text-gray-900 py-1')} {...filterProps(props)} />
);

const Img = ({ src, alt }: { src?: string; alt?: string }) => {
  const [error, setError] = useState(false);
  if (!src) return null;
  return (
    <div className="w-full max-w-xl my-2">
      {error ? (
        <div className="flex h-32 flex-col items-center justify-center gap-2 rounded-md bg-gray-50 text-gray-400 border border-gray-200">
          <p className="text-xs">Image unavailable</p>
        </div>
      ) : (
        // eslint-disable-next-line jsx-a11y/alt-text
        <img
          src={src}
          alt={alt ?? 'Rendered image'}
          className="w-full h-auto rounded-md object-cover shadow-sm"
          onError={() => setError(true)}
        />
      )}
    </div>
  );
};

export const inlineComponents = {
  h1: Heading1,
  h2: Heading2,
  h3: Heading3,
  h4: Heading4,
  h5: Heading5,
  h6: Heading6,
  ul: UnorderedList,
  ol: OrderedList,
  li: ListItem,
  em: EmphasizedText,
  i: ItalicText,
  strong: StrongText,
  b: BoldText,
  u: UnderlinedText,
  del: DeletedText,
  hr: HorizontalRule,
  blockquote: Blockquote,
  a: AnchorLink,
  img: Img,
  p: Paragraph,
  code: InlineCode, // Added Code support for inline
};

export default inlineComponents;
